import { DAO_URL } from '@/config/constants.ts'
import { testIdFactory } from '@/utils/test-id-factory.ts'
import { useLayoutEffect, useRef } from 'react'

/**
 * Exposes the bar's live height as the `--announcement-h` CSS variable on the
 * document root, so the fixed nav offset and the page top padding can follow
 * the bar even when its text wraps onto a second line.
 */
const useAnnouncementHeightVar = (ref: React.RefObject<HTMLElement>) => {
	useLayoutEffect(() => {
		const el = ref.current
		if (!el) return

		const root = document.documentElement
		const update = () => root.style.setProperty('--announcement-h', `${el.offsetHeight}px`)

		update()
		const observer = new ResizeObserver(update)
		observer.observe(el)

		return () => {
			observer.disconnect()
			root.style.removeProperty('--announcement-h')
		}
	}, [ref])
}

const AnnouncementBar = () => {
	const ref = useRef<HTMLAnchorElement>(null)
	useAnnouncementHeightVar(ref)

	return (
		<a
			ref={ref}
			href={DAO_URL}
			target="_blank"
			rel="noopener noreferrer"
			data-testid={testIdFactory.announcementBarLink()}
			className="group fixed top-0 left-0 z-50 flex min-h-10 w-full items-center justify-center gap-2.5 overflow-hidden bg-danger bg-gradient-to-r from-danger via-success to-danger bg-[length:200%_100%] px-6 py-1.5 text-center text-neutral motion-safe:animate-gradient-pan"
		>
			<span className="hidden shrink-0 text-[1.1rem] motion-safe:animate-wiggle sm:inline" aria-hidden>
				🌶️
			</span>
			<span className="text-balance text-[0.85rem] font-bold uppercase leading-tight drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)] sm:text-[1rem] md:text-[1.25rem]">
				PEPPER treasury &amp; voting have moved to the new PEPPER DAO, supervised by the PEPPER Ministers —{' '}
				<span className="underline decoration-2 underline-offset-4">Explore the DAO</span>
			</span>
			<span className="hidden shrink-0 text-[1.1rem] motion-safe:animate-wiggle sm:inline" aria-hidden>
				🌶️
			</span>
			<span
				className="pointer-events-none absolute inset-y-0 left-0 w-1/4 -skew-x-12 bg-white/30 blur-md motion-safe:animate-shine"
				aria-hidden
			/>
		</a>
	)
}

export default AnnouncementBar
